import { BRIEF_STATUS } from 'src/data/brief/briefStatus';
import { queryBrief, queryBriefStatus } from 'src/graphql/brief';
import { promisedWait } from 'src/lib/common';
import { AbortError, isAbortError } from 'src/lib/errors';
import { captureException } from 'src/lib/sentry';

const STATUS_POLLING_INTERVAL = 1_000; // 1 second
const EXTRACTION_TIMEOUT = 60_000; // 1 minute

class BriefExtractionError extends Error {
  constructor(code, message) {
    super(message);
    this.name = 'BriefExtractionError';
    this.code = code;
    this.message = message;
  }
}

const unwrapErrors = (json) => {
  try {
    return JSON.parse(json);
  } catch (error) {
    captureException(error, {
      extra: {
        source: 'services/waitForExtraction.js -> unwrapErrors',
        value: json,
      },
    });
    return [];
  }
};

const stringifyErrors = (errors) => errors.map((e) => e.detail).join('\n');

const checkKnownBriefExtractionError = (errors, errorCode, newMessage = null) => {
  const error = errors.find((e) => e.code === errorCode);
  if (error) {
    const message = newMessage || error.detail;
    return new BriefExtractionError(error.code, message);
  }
  return null;
};

const BRIEF_EXTRACTION_ERROR = {
  clientBriefApprovalNeeded: 'CLIENT_BRIEF_APPROVAL_NEEDED',
};

const KNOWN_ERRORS = [{ code: BRIEF_EXTRACTION_ERROR.clientBriefApprovalNeeded }];

const searchForKnownBriefExtractionError = (errors) => {
  for (let i = 0; i < KNOWN_ERRORS.length; i += 1) {
    const knownErrorData = KNOWN_ERRORS[i];
    const error = checkKnownBriefExtractionError(
      errors,
      knownErrorData.code,
      knownErrorData.message
    );
    if (error) {
      return error;
    }
  }

  return null;
};

const executeExtractionCycle = async (briefId, options = null) => {
  while (true) {
    // HINT: we don't pass "abort controller" into query itself, cause `apollo` does not work well with aborting queries
    if (options?.abortSignal?.aborted) {
      throw new AbortError();
    }

    // Check extraction status
    const result = await queryBriefStatus(briefId);

    // Clean success — query data directly
    if (result.status === BRIEF_STATUS.done) {
      return queryBrief(briefId);
    }

    // Extraction finished with errors — may still have usable data
    if (result.status === BRIEF_STATUS.doneWithErrors) {
      const errors = unwrapErrors(result.errors);
      const knownError = searchForKnownBriefExtractionError(errors);
      if (knownError) {
        return queryBrief(briefId);
      }
      throw new Error(stringifyErrors(errors));
    }

    // Wait some time and check again
    await promisedWait(STATUS_POLLING_INTERVAL);
  }
};

export const waitForBriefExtraction = async (briefId, options = null) => {
  const timeout = options?.timeout || EXTRACTION_TIMEOUT;
  const abortController = new AbortController();
  let timedOut = false;

  // Forward caller's abort signal to our internal controller
  const callerSignal = options?.abortSignal;
  if (callerSignal?.aborted) {
    abortController.abort();
  } else {
    callerSignal?.addEventListener('abort', () => abortController.abort(), {
      once: true,
    });
  }

  const timerId = setTimeout(() => {
    timedOut = true;
    abortController.abort();
  }, timeout);

  const cycleOptions = { ...options, abortSignal: abortController.signal };

  try {
    return await executeExtractionCycle(briefId, cycleOptions);
  } catch (error) {
    if (timedOut && isAbortError(error)) {
      throw new Error('Brief extraction timed out');
    }
    throw error;
  } finally {
    clearTimeout(timerId);
  }
};
